<template>
    <v-dialog v-model="dialog" max-width="600" scrollable aria-label="Remove pending invitation">
        <template v-slot:activator="{ on, attrs }">
            <v-btn
                v-if="tableView"
                class="branch-trigger"
                color="#ffaaa3"
                elevation="0"
                outlined
                width="80px"
                small
                v-bind="attrs"
                v-on="on"
                >Remove</v-btn
            >
            <v-btn
                v-if="!tableView"
                class="branch-trigger branch-trigger--tree"
                color="#ffaaa3"
                outlined
                elevation="0"
                v-bind="attrs"
                v-on="on"
            >
                Remove User
            </v-btn>
        </template>
        <v-card class="branch-dialog" dark elevation="0">
            <v-card-title class="branch-dialog__header">
                <h2>Remove pending invitation?</h2>
            </v-card-title>
            <v-card-text class="branch-dialog__body">
                <p class="branch-dialog__warning">This user has not yet accepted the organization invite.</p>
                <p class="branch-dialog__intro"
                    >To remove this user, type the email address "{{ currentAccount.requestedEmail }}" below.</p
                >
                <v-text-field v-model="confirmDelete" outlined dense color="#f0784c" label="Email" autocomplete="off" />
            </v-card-text>
            <v-card-actions class="branch-dialog__actions">
                <v-btn class="branch-secondary" outlined @click="dialog = false">Cancel</v-btn>
                <v-btn class="branch-danger" outlined elevation="0" :disabled="confirmDeletion" @click="deleteUser">
                    Remove {{ currentAccount.requestedEmail }}
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-snackbar v-model="snackbar">
            {{ snackbarMessage }}

            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" color="white" @click="snackbar = false"> Close </v-btn>
            </template>
        </v-snackbar>
    </v-dialog>
</template>

<script>
import { mdiAccountSupervisor } from '@mdi/js'
import gql from 'graphql-tag'

export default {
    name: 'UpdateUser',
    props: {
        currentLevel: {
            type: Object,
            required: true
        },
        currentAccount: {
            type: Object,
            required: true
        },
        tableView: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            dialog: false,
            confirmDialog: false,
            confirmDelete: '',
            orgName: '',
            snackbar: false,
            snackbarMessage: '',
            isAdmin: this.currentAccount.admin,
            accountEmail: '',
            submitted: false,
            mdiAccountSupervisor
        }
    },
    computed: {
        confirmDeletion() {
            if (this.confirmDelete === this.currentAccount.requestedEmail) {
                return false
            } else {
                return true
            }
        }
    },
    methods: {
        deleteUser() {
            this.$apollo
                .mutate({
                    mutation: gql`
                        mutation RemoveUserFromOrganization($orgId: String!, $email: String, $userId: String) {
                            removeUserFromOrganization(orgId: $orgId, email: $email, userId: $userId) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        orgId: this.currentLevel.baseAccountId,
                        email: this.currentAccount.requestedEmail,
                        userId: this.currentAccount.accountId
                    }
                })
                .then((data) => {
                    if (data.data.removeUserFromOrganization.success === true) {
                        this.$emit('deleted', `'${this.currentAccount.requestedEmail}' successfully deleted`)
                        this.confirmDelete = ''
                        this.dialog = false
                    } else {
                        this.snackbar = true
                        this.snackbarMessage = `Error deleting '${this.currentAccount.requestedEmail}'`
                    }
                })
        }
    }
}
</script>

<style scoped>
.branch-trigger,
.branch-dialog {
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 12px;
}
.branch-trigger {
    border-radius: 8px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-trigger--tree {
    display: block;
    margin: auto;
}
.branch-dialog.v-card {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
    border: 1px solid #454953;
    border-radius: 12px;
    background: #282b33;
    color: #f5f5f7;
}
.branch-dialog__header {
    flex: 0 0 auto;
    padding: 20px 24px;
    border-bottom: 1px solid #454953;
}
.branch-dialog h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
}
.branch-dialog .branch-dialog__body {
    min-height: 0;
    padding: 20px 24px;
    overflow-y: auto;
    color: #d4d6dd;
    font-size: 12px;
    line-height: 1.6;
}
.branch-dialog__intro {
    margin-bottom: 20px;
    overflow-wrap: anywhere;
}
.branch-dialog__warning {
    color: #ffaaa3;
}
.branch-dialog__actions {
    flex: 0 0 auto;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 24px;
    border-top: 1px solid #454953;
    background: #22252d;
}
.branch-dialog .v-btn {
    max-width: 100%;
    height: auto;
    min-height: 36px;
    margin: 0;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-dialog ::v-deep .v-btn__content {
    white-space: normal;
    overflow-wrap: anywhere;
}
.branch-dialog .branch-secondary {
    border-color: #686d79;
    color: #e0e2e8;
}
.branch-dialog .branch-danger {
    border-color: #a45c58;
    color: #ffaaa3;
}
.branch-dialog ::v-deep .v-input,
.branch-dialog ::v-deep .v-label,
.branch-dialog ::v-deep input {
    font-family: inherit;
    font-size: 12px;
}
.branch-dialog ::v-deep .v-text-field--outlined .v-input__slot {
    background: #22252d;
    border-radius: 8px;
}
.branch-dialog ::v-deep .v-text-field--outlined:not(.v-input--is-focused):not(.error--text) fieldset {
    border-color: #686d79;
}
.branch-trigger:focus-visible,
.branch-dialog ::v-deep .v-btn:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 600px) {
    .branch-dialog__header,
    .branch-dialog .branch-dialog__body,
    .branch-dialog__actions {
        padding: 16px;
    }
    .branch-dialog h2 {
        font-size: 20px;
    }
}
</style>
